/* Hero rotating word + slide-out panel. */

class PotpRotator extends HTMLElement {
  connectedCallback() {
    this.words = Array.from(this.querySelectorAll('[data-rot-word]'));
    if (this.words.length < 2) return;

    this.index = 0;
    this.interval = (parseFloat(this.dataset.interval) || 1.5) * 1000;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.inner = this.querySelector('.potp-rot__inner');

    // The grid cell would otherwise stay as wide as the longest word, leaving
    // short words floating in dead space and knocking the centred headline
    // off balance. Drive the width from the active word instead.
    this.measure();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.measure());
    }
    this.onResize = () => this.measure();
    window.addEventListener('resize', this.onResize);

    const trigger = this.querySelector('[data-rot-trigger]');
    if (trigger && this.dataset.opens) {
      trigger.addEventListener('click', () => {
        const panel = document.getElementById(this.dataset.opens);
        if (panel && panel.open) panel.open(trigger);
      });
    }

    // Pause while someone is reading or interacting with it.
    this.addEventListener('pointerenter', () => this.stop());
    this.addEventListener('pointerleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());

    // Only animate when actually on screen.
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? this.start() : this.stop()));
    });
    this.observer.observe(this);

    this.onMotionChange = () => (this.reduced.matches ? this.stop() : this.start());
    this.reduced.addEventListener('change', this.onMotionChange);
  }

  disconnectedCallback() {
    this.stop();
    if (this.observer) this.observer.disconnect();
    if (this.reduced) this.reduced.removeEventListener('change', this.onMotionChange);
    window.removeEventListener('resize', this.onResize);
  }

  // Words share one grid cell, so their own boxes all report the cell width.
  // Measure the text with an offscreen probe that copies their type styles.
  measure() {
    if (!this.inner || !this.words.length) return;
    const cs = window.getComputedStyle(this.words[0]);
    const probe = document.createElement('span');
    probe.style.cssText = 'position:absolute;left:-9999px;top:0;white-space:nowrap;';
    probe.style.fontFamily = cs.fontFamily;
    probe.style.fontSize = cs.fontSize;
    probe.style.fontWeight = cs.fontWeight;
    probe.style.fontStyle = cs.fontStyle;
    probe.style.letterSpacing = cs.letterSpacing;
    document.body.appendChild(probe);

    this.widths = this.words.map((w) => {
      probe.textContent = w.textContent.trim();
      // small buffer: sub-pixel rounding can leave the text a few px wider
      return Math.ceil(probe.getBoundingClientRect().width) + 4;
    });
    probe.remove();

    this.applyWidth();
  }

  // Desktop reserves the longest word's width so the static text beside it never
  // shifts. On mobile the word wraps onto its own line, so that reservation just
  // strands it against the left edge — size to the active word and let the
  // centred line do the work instead.
  applyWidth() {
    if (!this.inner || !this.widths) return;
    const perWord = window.matchMedia('(max-width: 767px)').matches;
    const w = perWord ? this.widths[this.index] : Math.max.apply(null, this.widths);
    this.inner.style.width = w + 'px';
  }

  start() {
    if (this.timer || this.reduced.matches || this.words.length < 2) return;
    this.timer = window.setInterval(() => this.next(), this.interval);
  }

  stop() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  next() {
    const current = this.words[this.index];
    this.index = (this.index + 1) % this.words.length;
    const upcoming = this.words[this.index];

    current.removeAttribute('data-active');
    current.setAttribute('data-leaving', '');
    window.setTimeout(() => current.removeAttribute('data-leaving'), 400);

    upcoming.setAttribute('data-active', 'true');
    this.applyWidth();
  }
}

class PotpPanel extends HTMLElement {
  connectedCallback() {
    this.sheet = this.querySelector('.potp-panel__sheet');
    this.querySelectorAll('[data-panel-close]').forEach((el) =>
      el.addEventListener('click', () => this.close())
    );

    this.onKey = (event) => {
      if (event.key === 'Escape' && this.hasAttribute('data-open')) this.close();
    };
    document.addEventListener('keydown', this.onKey);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKey);
    document.body.classList.remove('potp-panel-open');
  }

  // Visibility is handled entirely in CSS off [data-open]; no requestAnimationFrame
  // or hidden-attribute juggling, which failed to fire when the page wasn't
  // actively painting and left the panel invisible but scroll-locked.
  open(opener) {
    this.opener = opener;
    this.setAttribute('data-open', 'true');
    document.body.classList.add('potp-panel-open');
    if (this.sheet) this.sheet.focus();
  }

  close() {
    this.removeAttribute('data-open');
    document.body.classList.remove('potp-panel-open');
    if (this.opener) this.opener.focus();
  }
}

customElements.define('potp-rotator', PotpRotator);
customElements.define('potp-panel', PotpPanel);
